var _Dictionary_hashtable, _EnumeratorEnumerable_enumerator, _LinkedList_root, _LinkedList_current, _LinkedList_count, _EnumerableGroup_key, _EnumerableGroup_enumerable, _RangeEnumerable_start, _RangeEnumerable_count;
import { __classPrivateFieldGet, __classPrivateFieldSet } from "tslib";
export * from './_types';
import DefaultComparers from "../Comparers/DefaultComparers";
import MapComparer from "../Comparers/MapComparer";
import ReverseComparer from "../Comparers/ReverseComparer";
import AppendEnumerator from "../Enumerators/AppendEnumerator";
import ArrayEnumerator from "../Enumerators/ArrayEnumerator";
import DictionaryEnumerator from "../Enumerators/DictionaryEnumerator";
import RangeEnumerator from "../Enumerators/RangeEnumerator";
import SelectEnumerator from "../Enumerators/SelectEnumerator";
import SelectManyEnumerator from "../Enumerators/SelectManyEnumerator";
import SkipEnumerator from "../Enumerators/SkipEnumerator";
import TakeEnumerator from "../Enumerators/TakeEnumerator";
import WhereEnumerator from "../Enumerators/WhereEnumerator";
import LinkedListEnumerator from "../Enumerators/LinkedListEnumerator";
import getHash from '../Utilities/getHash';
import isUndefinedOrNull from '../TypeHelpers/isUndefinedOrNull';
import ArgumentException from '../Exceptions/ArgumentException';
import KeyAlreadyDefinedException from '../Exceptions/KeyAlreadyDefinedException';
import KeyNotFoundException from '../Exceptions/KeyNotFoundException';
import equivilentToByJSON from '../Utilities/equivilentToByJSON';
import contains from './utils/IEnumerable/contains';
import forEach from './utils/IEnumerable/forEach';
import item from './utils/IEnumerable/item';
import all from './utils/IEnumerable/all';
import any from './utils/IEnumerable/any';
import distinct from './utils/IEnumerable/distinct';
import count from './utils/IEnumerable/count';
import average from './utils/IEnumerable/average';
import first from './utils/IEnumerable/first';
import firstOrDefault from './utils/IEnumerable/firstOrDefault';
import last from './utils/IEnumerable/last';
import lastOrDefault from './utils/IEnumerable/lastOrDefault';
import single from './utils/IEnumerable/single';
import singleOrDefault from './utils/IEnumerable/singleOrDefault';
import max from './utils/IEnumerable/max';
import min from './utils/IEnumerable/min';
import ofType from './utils/IEnumerable/ofType';
import asArray from './utils/asArray';
import remove from './utils/Collection/remove';
import copyTo from './utils/Collection/copyTo';
import { InvalidOperationException, NotImplementedException } from '../Exceptions';
import { where } from '../Arrays';
import sum from './utils/IEnumerable/sum';
import toArray from './utils/IEnumerable/toArray';
export class Enumerable {
    static range(start, count) {
        //return new EnumeratorEnumerable(new RangeEnumerator(start, count));
        return new RangeEnumerable(start, count);
    }
    static empty() {
        return Enumerable.asEnumerable([]);
    }
    static asArray(enumerableOrArray) {
        return asArray(enumerableOrArray);
    }
    static asEnumerable(enumerableOrArray) {
        if (Array.isArray(enumerableOrArray)) {
            return new ArrayEnumerable([...enumerableOrArray]);
        }
        else {
            return enumerableOrArray;
        }
    }
}
export class EnumerableBase {
    [Symbol.iterator]() {
        return this.getEnumerator();
    }
    append(item) {
        return this.concat(Enumerable.asEnumerable([item]));
    }
    concat(next) {
        return new EnumeratorEnumerable(new AppendEnumerator(this.getEnumerator(), next.getEnumerator()));
    }
    prepend(item) {
        return new EnumeratorEnumerable(new AppendEnumerator(new ArrayEnumerator([item]), this.getEnumerator()));
    }
    contains(item) {
        return contains(this, item);
    }
    forEach(callback) {
        return forEach(this, callback);
    }
    item(index) {
        return item(this, index);
    }
    all(predicate) {
        return all(this, predicate);
    }
    any(predicate) {
        return any(this, predicate);
    }
    average(selector) {
        return average(this, selector);
    }
    count() {
        return count(this);
    }
    // USAGE: obj.Distinct(); or obj.Distinct(['key1'],['key2']);
    distinct(selector) {
        return distinct(this, selector);
    }
    first(predicate) {
        return first(this, predicate);
    }
    firstOrDefault(predicate) {
        return firstOrDefault(this, predicate);
    }
    groupBy(selector, comparer = DefaultComparers.DefaultComparer) {
        const keySet = this.select(selector).distinct((k) => k).orderBy(k => k);
        return keySet.select((key) => new EnumerableGroup(this, key, selector, comparer));
    }
    // public join<TInner, TKey, TResult>(
    //     inner: IEnumerable<TInner>,
    //     outerKeySelector: (outer: T) => TKey,
    //     innerKeySelector: (inner: TInner) => TKey,
    //     resultSelector: (outer: T, inner: TInner) => TResult): IEnumerable<TResult>
    // {
    //     return this
    //         .select(o => ({
    //             value: o,
    //             key: outerKeySelector(o)
    //         }))
    //         .selectMany(o =>
    //             inner
    //                 .select(i => ({
    //                     inner: i,
    //                     outer: o.value,
    //                     key: innerKeySelector(i)
    //                 }))
    //                 .where(i => i.key == o.key)
    //         )
    //         .select(j => resultSelector(j.outer, j.inner))
    //         ;
    // }
    last(predicate) {
        return last(this, predicate);
    }
    lastOrDefault(predicate) {
        return lastOrDefault(this, predicate);
    }
    max(selector) {
        return max(this, selector);
    }
    min(selector) {
        return min(this, selector);
    }
    ofType(ctor) {
        return ofType(this, ctor);
    }
    orderBy(selector, comparer) {
        return this.internalOrderBy(selector, comparer || DefaultComparers.DefaultComparer);
    }
    orderByDescending(selector, comparer) {
        return this.internalOrderBy(selector, new ReverseComparer(comparer || DefaultComparers.DefaultComparer));
    }
    select(selector) {
        return new EnumeratorEnumerable(new SelectEnumerator(this.getEnumerator(), selector));
    }
    selectMany(selector) {
        return new EnumeratorEnumerable(new SelectManyEnumerator(this.getEnumerator(), selector));
    }
    single(predicate) {
        return single(this, predicate);
    }
    singleOrDefault(predicate) {
        return singleOrDefault(this, predicate);
    }
    skip(count) {
        return new EnumeratorEnumerable(new SkipEnumerator(this.getEnumerator(), count));
    }
    split(predicate) {
        return {
            pTrue: this.where(i => predicate(i)),
            pFalse: this.where(i => !predicate(i))
        };
    }
    sum(selector) {
        return sum(this, selector);
    }
    take(count) {
        return new EnumeratorEnumerable(new TakeEnumerator(this.getEnumerator(), count));
    }
    where(predicate) {
        return new EnumeratorEnumerable(new WhereEnumerator(this.getEnumerator(), predicate));
    }
    toArray() {
        return toArray(this);
    }
    toDictionary(keySelector, valueSelector) {
        const dictionary = new Dictionary();
        this.forEach(i => {
            dictionary.addKeyValue(keySelector(i), valueSelector(i));
        });
        return dictionary;
    }
    toList() {
        return new List(this);
    }
    internalOrderBy(selector, comparer) {
        // HACK: this could be better...
        const list = this.toList();
        const mapComparer = new MapComparer(selector, comparer);
        list.sort(mapComparer);
        return list;
    }
}
export class ArrayEnumerable {
    [Symbol.iterator]() {
        return this._array[Symbol.iterator]();
    }
    constructor(array) {
        this._array = array;
    }
    /**@inheritDoc */
    all(predicate) {
        return all(this._array, predicate);
    }
    /**@inheritDoc */
    append(item) {
        return new ArrayEnumerable([...this, item]);
    }
    /**@inheritDoc */
    any(predicate) {
        return any(this._array, predicate);
    }
    /**@inheritDoc */
    average(selector) {
        return average(new ArrayEnumerable(this._array), selector);
    }
    /**@inheritDoc */
    concat(next) {
        return new ArrayEnumerable([...this._array, ...next]);
    }
    /**@inheritDoc */
    contains(item) {
        return contains(this._array, item);
    }
    /**@inheritDoc */
    count() {
        return count(this._array);
    }
    /**@inheritDoc */
    distinct(selector) {
        return distinct(new ArrayEnumerable(this._array), selector);
    }
    /**@inheritDoc */
    first(predicate) {
        const result = this.firstOrDefault(predicate);
        if (isUndefinedOrNull(result)) {
            throw new InvalidOperationException('There are no results');
        }
        return result;
    }
    /**@inheritDoc */
    firstOrDefault(predicate) {
        const result = isUndefinedOrNull(predicate)
            ? this._array[0]
            : this._array.find(predicate);
        return isUndefinedOrNull(result)
            ? null
            : result;
    }
    /**@inheritDoc */
    forEach(callback) {
        return forEach(this._array, callback);
    }
    /**@inheritDoc */
    getEnumerator() {
        return new ArrayEnumerator(this._array);
    }
    /**@inheritDoc */
    groupBy(keySelector, comparer = DefaultComparers.DefaultComparer) {
        const keySet = this.select(keySelector).distinct((k) => k).orderBy(k => k).toArray();
        const result = keySet.reduce((group, key) => {
            const en = this.where(item => comparer.equals(keySelector(item), key));
            en['key'] = key;
            group.push(en);
            return group;
        }, new Array());
        return new ArrayEnumerable(result);
    }
    /**@inheritDoc */
    item(index) {
        return this._array[index];
    }
    /**@inheritDoc */
    last(predicate) {
        const result = this.lastOrDefault(predicate);
        if (isUndefinedOrNull(result)) {
            throw new InvalidOperationException('There are no results');
        }
        return result;
    }
    /**@inheritDoc */
    lastOrDefault(predicate) {
        if (isUndefinedOrNull(predicate)) {
            return this._array[this._array.length - 1] || null;
        }
        const set = this._array.filter(predicate);
        const result = set[set.length - 1];
        return isUndefinedOrNull(result)
            ? null
            : result;
    }
    /**@inheritDoc */
    max(selector) {
        return Math.max(...this._array.map(selector));
    }
    /**@inheritDoc */
    min(selector) {
        return Math.min(...this._array.map(selector));
    }
    /**@inheritDoc */
    ofType(ctor) {
        return ofType(new ArrayEnumerable(this._array), ctor);
    }
    /**@inheritDoc */
    orderBy(selector, comparer) {
        return this.internalOrderBy(selector, comparer || DefaultComparers.DefaultComparer);
    }
    /**@inheritDoc */
    orderByDescending(selector, comparer) {
        return this.internalOrderBy(selector, new ReverseComparer(comparer || DefaultComparers.DefaultComparer));
    }
    internalOrderBy(selector, comparer) {
        // HACK: this could be better...
        const array = this.toArray();
        const mapComparer = new MapComparer(selector, comparer);
        array.sort((a, b) => mapComparer.compare(a, b));
        return new ArrayEnumerable(array);
    }
    /**@inheritDoc */
    prepend(item) {
        return new ArrayEnumerable([item, ...this._array]);
    }
    /**@inheritDoc */
    select(selector) {
        return new ArrayEnumerable(this._array.map(selector));
    }
    /**@inheritDoc */
    selectMany(selector) {
        return new ArrayEnumerable(this._array.reduce((results, t) => [...results, ...selector(t)], []));
    }
    /**@inheritDoc */
    single(predicate) {
        const result = this.singleOrDefault(predicate);
        if (isUndefinedOrNull(result)) {
            throw new InvalidOperationException('There is no result.');
        }
        return result;
    }
    /**@inheritDoc */
    singleOrDefault(predicate) {
        const result = isUndefinedOrNull(predicate)
            ? this._array
            : this._array.filter(predicate);
        if (result.length > 1) {
            throw new InvalidOperationException('More than one match in the collection.');
        }
        if (result.length !== 1) {
            return null;
        }
        return result[0];
    }
    /**@inheritDoc */
    skip(count) {
        return new ArrayEnumerable(this._array.slice(count));
    }
    /**@inheritDoc */
    split(predicate) {
        return {
            pTrue: this.where(i => predicate(i)),
            pFalse: this.where(i => !predicate(i))
        };
    }
    /**@inheritDoc */
    sum(selector) {
        return this._array
            .map((item) => selector(item))
            .reduce((a, c) => a + c, 0);
    }
    /**@inheritDoc */
    take(count) {
        return new ArrayEnumerable(this._array.slice(0, count));
    }
    /**@inheritDoc */
    toArray() {
        return [...this._array];
    }
    /**@inheritDoc */
    toDictionary(keySelector, valueSelector) {
        return this._array.reduce((dict, val) => {
            dict.addKeyValue(keySelector(val), valueSelector(val));
            return dict;
        }, new Dictionary());
    }
    /**@inheritDoc */
    toList() {
        throw new NotImplementedException();
    }
    /**@inheritDoc */
    where(predicate) {
        return new ArrayEnumerable(where(this._array, predicate));
    }
}
export class Collection extends ArrayEnumerable {
    constructor(enumerableOrArray) {
        if (isUndefinedOrNull(enumerableOrArray)) {
            super([]);
        }
        else {
            super(Enumerable.asArray(enumerableOrArray));
        }
    }
    get isReadOnly() {
        return false;
    }
    add(obj) {
        this._array.push(obj);
    }
    get length() {
        return this._array.length;
    }
    clear() {
        this._array.length = 0;
    }
    copyTo(array, arrayIndex) {
        return copyTo(this._array, array, arrayIndex);
    }
    remove(item) {
        return remove(this._array, item);
    }
}
export class Dictionary extends EnumerableBase {
    // constructor
    constructor(enumerableOrArray) {
        super();
        _Dictionary_hashtable.set(this, void 0);
        this.isReadOnly = false;
        __classPrivateFieldSet(this, _Dictionary_hashtable, {}, "f");
        if (enumerableOrArray) {
            const items = Enumerable.asArray(enumerableOrArray);
            for (const item of items) {
                this.add(item);
            }
        }
    }
    get length() {
        return Object.keys(__classPrivateFieldGet(this, _Dictionary_hashtable, "f")).length;
    }
    get keys() {
        return Object.keys(__classPrivateFieldGet(this, _Dictionary_hashtable, "f")).map(key => __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[key].key);
    }
    get values() {
        return Object.keys(__classPrivateFieldGet(this, _Dictionary_hashtable, "f")).map(key => __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[key].value);
    }
    add(keyValuePair) {
        const hash = getHash(keyValuePair.key);
        if (__classPrivateFieldGet(this, _Dictionary_hashtable, "f")[getHash(keyValuePair.key)] !== undefined) {
            throw new KeyAlreadyDefinedException(keyValuePair.key);
        }
        __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash] = keyValuePair;
    }
    addKeyValue(key, value) {
        this.add({ key: key, value: value });
    }
    itemByKey(key) {
        const hash = getHash(key);
        if (__classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash] === undefined) {
            throw new KeyNotFoundException(key);
        }
        return __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash].value;
    }
    containsKey(key) {
        const hash = getHash(key);
        return __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash] !== undefined;
    }
    removeByKey(key) {
        const hash = getHash(key);
        if (__classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash] === undefined) {
            return false;
        }
        delete __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash];
        return true;
    }
    tryGetValue(key) {
        try {
            return {
                value: this.itemByKey(key),
                success: true
            };
        }
        catch {
            return { success: false };
        }
    }
    clear() {
        __classPrivateFieldSet(this, _Dictionary_hashtable, {}, "f");
    }
    remove(item) {
        const hash = getHash(item.key);
        if (__classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash] === undefined || __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash].value !== item.value) {
            return false;
        }
        delete __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash];
        return true;
    }
    contains(item) {
        const hash = getHash(item.key);
        if (__classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash] === undefined || __classPrivateFieldGet(this, _Dictionary_hashtable, "f")[hash].value !== item.value) {
            return false;
        }
        return true;
    }
    copyTo(array, arrayIndex) {
        array.splice(arrayIndex, this.length, ...this.toArray());
    }
    getEnumerator() {
        return new DictionaryEnumerator(this);
    }
}
_Dictionary_hashtable = new WeakMap();
export class EnumeratorEnumerable extends EnumerableBase {
    constructor(enumerator) {
        super();
        _EnumeratorEnumerable_enumerator.set(this, void 0);
        __classPrivateFieldSet(this, _EnumeratorEnumerable_enumerator, enumerator, "f");
    }
    getEnumerator() {
        return __classPrivateFieldGet(this, _EnumeratorEnumerable_enumerator, "f");
    }
}
_EnumeratorEnumerable_enumerator = new WeakMap();
export class LinkedList extends EnumerableBase {
    constructor(enumerableOrArray) {
        super();
        _LinkedList_root.set(this, void 0);
        _LinkedList_current.set(this, void 0);
        _LinkedList_count.set(this, void 0);
        __classPrivateFieldSet(this, _LinkedList_count, 0, "f");
        if (enumerableOrArray) {
            for (const item of Enumerable.asArray(enumerableOrArray)) {
                this.add(item);
            }
        }
    }
    append(item) {
        return this.concat(Enumerable.asEnumerable([item]));
    }
    concat(next) {
        return new EnumeratorEnumerable(new AppendEnumerator(this.getEnumerator(), next.getEnumerator()));
    }
    prepend(item) {
        return Enumerable.asEnumerable([item]).concat(this);
    }
    get length() {
        return __classPrivateFieldGet(this, _LinkedList_count, "f");
    }
    get isReadOnly() {
        return false;
    }
    add(item) {
        var _a;
        const listItem = {
            value: item
        };
        if (__classPrivateFieldGet(this, _LinkedList_current, "f") === undefined) {
            __classPrivateFieldSet(this, _LinkedList_current, __classPrivateFieldSet(this, _LinkedList_root, listItem, "f"), "f");
        }
        else {
            __classPrivateFieldGet(this, _LinkedList_current, "f").next = listItem;
            __classPrivateFieldSet(this, _LinkedList_current, listItem, "f");
        }
        __classPrivateFieldSet(this, _LinkedList_count, (_a = __classPrivateFieldGet(this, _LinkedList_count, "f"), _a++, _a), "f");
    }
    clear() {
        __classPrivateFieldSet(this, _LinkedList_current, __classPrivateFieldSet(this, _LinkedList_root, undefined, "f"), "f");
        __classPrivateFieldSet(this, _LinkedList_count, 0, "f");
    }
    contains(item) {
        if (__classPrivateFieldGet(this, _LinkedList_root, "f") === undefined) {
            return false;
        }
        let result = false;
        this.traverse(__classPrivateFieldGet(this, _LinkedList_root, "f"), node => !(result = (node.value === item)));
        return result;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    copyTo(array, arrayIndex) {
        // TODO:   throw new Error("Error/Method not implemented.");
        throw new Error("copyTo Error/Method not implemented.");
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    remove(item) {
        // TODO:   throw new Error("Error/Method not implemented.");
        throw new Error("remove Error/Method not implemented.");
    }
    forEach(callback) {
        let index = 0;
        if (__classPrivateFieldGet(this, _LinkedList_root, "f") === undefined) {
            return;
        }
        return this.traverse(__classPrivateFieldGet(this, _LinkedList_root, "f"), node => callback(node.value, index++));
    }
    getEnumerator() {
        return new LinkedListEnumerator(this);
    }
    item(index) {
        if (__classPrivateFieldGet(this, _LinkedList_root, "f") !== undefined) {
            if (index == 0) {
                return __classPrivateFieldGet(this, _LinkedList_root, "f").value;
            }
            let count = 0;
            let result;
            this.traverse(__classPrivateFieldGet(this, _LinkedList_root, "f"), node => {
                if (count == index) {
                    result = node.value;
                }
                count++;
            });
            return result;
        }
        else {
            return undefined;
        }
    }
    ofType(ctor) {
        return this.where((item) => item instanceof ctor).select((item) => item);
    }
    toArray() {
        const result = [];
        const en = this.getEnumerator();
        while (en.moveNext()) {
            result.push(en.current);
        }
        return result;
    }
    toDictionary(keySelector, valueSelector) {
        return new Dictionary(this.toArray().map(i => ({ key: keySelector(i), value: valueSelector(i) })));
    }
    toList() {
        return new List(this);
    }
    /**
     * Traverse the collection, return false in the callback to break. return true or undefined to continue.
     * @param node
     * @param callback
     */
    traverse(node, callback) {
        const cont = callback(node);
        if (cont !== false && node.next !== undefined) {
            return this.traverse(node.next, callback);
        }
    }
}
_LinkedList_root = new WeakMap(), _LinkedList_current = new WeakMap(), _LinkedList_count = new WeakMap();
export class List extends Collection {
    addRange(enumerableOrArray) {
        const array = Enumerable.asArray(enumerableOrArray);
        this._array = this._array.concat(array);
    }
    find(obj, isEquivilent = false) {
        if (isEquivilent) {
            for (const item of this._array) {
                if (equivilentToByJSON(item, obj)) {
                    return item;
                }
            }
        }
        else {
            const index = this.indexOf(obj);
            if (index !== undefined) {
                return this.item(index);
            }
            else {
                return undefined;
            }
        }
    }
    indexOf(value, isEquivilent = false) {
        if (isEquivilent) {
            let index;
            this.forEach((item, i) => {
                if (equivilentToByJSON(item, value)) {
                    index = i;
                    return false;
                }
            });
            return index;
        }
        else {
            const index = this._array.indexOf(value);
            if (index == -1) {
                return undefined;
            }
            return index;
        }
    }
    insert(obj, index) {
        this._array.splice(index, 0, obj);
    }
    prepend(obj) {
        this.insert(obj, 0);
        return this;
    }
    prependRange(enumerableOrArray) {
        const array = Enumerable.asArray(enumerableOrArray);
        this._array.splice(0, 0, ...array);
    }
    removeAt(index) {
        this._array.splice(index, 1);
    }
    sort(comparer = DefaultComparers.DefaultComparer) {
        this._array.sort((a, b) => comparer.compare(a, b));
    }
}
export default class Set extends Collection {
    constructor(enumerable) {
        super(enumerable);
    }
    get length() {
        return this.length;
    }
    get isReadOnly() {
        return super.isReadOnly;
    }
    append(item) {
        return this.concat(Enumerable.asEnumerable([item]));
    }
    concat(next) {
        return new EnumeratorEnumerable(new AppendEnumerator(this.getEnumerator(), next.getEnumerator()));
    }
    prepend(item) {
        return Enumerable.asEnumerable([item]).concat(this);
    }
    exceptWith(enumerable) {
        const en = enumerable.getEnumerator();
        while (en.moveNext()) {
            const item = en.current;
            if (this.contains(item)) {
                this.remove(item);
            }
        }
    }
    intersectWith(enumerable) {
        const array = enumerable.toArray();
        this.forEach((value /*, index*/) => {
            if (array.indexOf(value) == -1) {
                this.remove(value);
            }
        });
    }
    isSubsetOf(enumerable) {
        const array = enumerable.toArray();
        return this.toArray().every((value) => array.indexOf(value) > -1);
    }
    isSupersetOf(enumerable) {
        const array = enumerable.toArray();
        return array.every((value) => this.contains(value));
    }
    overlaps(enumerable) {
        const array = enumerable.toArray();
        return array.some((value) => this.contains(value));
    }
    setEquals(enumerable) {
        return this.isSubsetOf(enumerable) && this.isSupersetOf(enumerable);
    }
    symmetricExceptWith(enumerable) {
        // get the items that are only in both
        const intersect = new Set(new Collection(this.toArray()));
        intersect.intersectWith(enumerable);
        // union this with enumerable
        this.unionWith(enumerable);
        // remove the intersect from this
        this.exceptWith(intersect);
    }
    unionWith(enumerable) {
        const en = enumerable.getEnumerator();
        while (en.moveNext()) {
            const item = en.current;
            if (!this.contains(item)) {
                this.add(item);
            }
        }
    }
    /**
     * Adds an item to the ICollection<T>.
     * @param item The object to add to the ICollection<T>.
     * @throws {ArgumentException} When the item is already in the collection.
     */
    add(item) {
        if (!this.contains(item)) {
            this.add(item);
        }
        else {
            throw new ArgumentException('item', 'item already in set');
        }
    }
    clear() {
        this.clear();
    }
    contains(item) {
        return this.contains(item);
    }
    copyTo(array, arrayIndex) {
        this.copyTo(array, arrayIndex);
    }
    remove(item) {
        return this.remove(item);
    }
    forEach(callback) {
        this.forEach(callback);
    }
    getEnumerator() {
        return this.getEnumerator();
    }
    item(index) {
        return this.item(index);
    }
    ofType(ctor) {
        return this.ofType(ctor);
    }
    toArray() {
        return this.toArray();
    }
    toDictionary(keySelector, valueSelector) {
        return this.toDictionary(keySelector, valueSelector);
    }
    toList() {
        return this.toList();
    }
}
class EnumerableGroup extends EnumerableBase {
    constructor(parentEnumerable, key, keySelector, keyComparer) {
        super();
        _EnumerableGroup_key.set(this, void 0);
        _EnumerableGroup_enumerable.set(this, void 0);
        __classPrivateFieldSet(this, _EnumerableGroup_enumerable, parentEnumerable.where(item => keyComparer.equals(keySelector(item), key)), "f");
        __classPrivateFieldSet(this, _EnumerableGroup_key, key, "f");
    }
    get key() {
        return __classPrivateFieldGet(this, _EnumerableGroup_key, "f");
    }
    getEnumerator() {
        return __classPrivateFieldGet(this, _EnumerableGroup_enumerable, "f").getEnumerator();
    }
}
_EnumerableGroup_key = new WeakMap(), _EnumerableGroup_enumerable = new WeakMap();
class RangeEnumerable extends EnumerableBase {
    constructor(start, count) {
        super();
        _RangeEnumerable_start.set(this, void 0);
        _RangeEnumerable_count.set(this, void 0);
        __classPrivateFieldSet(this, _RangeEnumerable_start, start, "f");
        __classPrivateFieldSet(this, _RangeEnumerable_count, count, "f");
    }
    getEnumerator() {
        return new RangeEnumerator(__classPrivateFieldGet(this, _RangeEnumerable_start, "f"), __classPrivateFieldGet(this, _RangeEnumerable_count, "f"));
    }
}
_RangeEnumerable_start = new WeakMap(), _RangeEnumerable_count = new WeakMap();
